import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { type MessageProps } from '@post-office/placement-common/utils/message-renderer';

import { type renderer } from '../__generated__/client/render';

import {
	ROVO_JIRA_TWG_CLI_CTA_TREATMENT_PARAM,
	RovoJiraTwgCliCtaCohort,
} from '@post-office/browser--rovo-expansion/jira-twg-cli-cta/constants';

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

// Experiment cohort can be control, not-enrolled, or specific variation names
type ExperimentCohort = string;

type MessageExperimentConfiguration = Record<
	string,
	{
		experimentKey: string;
		/**
		 * List of specific variation cohort names that should show this message.
		 * Only users in these cohorts will see the message.
		 * Uses shared constants from message template to ensure consistency.
		 */
		allowedCohorts: readonly string[];
		/**
		 * Optional Statsig parameter name to read from the experiment.
		 * Defaults to 'cohort' if not specified.
		 */
		statsigParameterName?: string;
	}
>;

export type Message = MessageProps<typeof renderer>;

/**
 * Checks if the cohort is in the allowed list.
 * Note: This is case-sensitive - cohort names must match exactly as configured in the experiment.
 */
const isAllowedCohort = (cohort: ExperimentCohort, allowedCohorts: readonly string[]): boolean => {
	return allowedCohorts.includes(cohort);
};

export const messageExperimentConfigs: MessageExperimentConfiguration = {
	'jira-twg-cli-cta': {
		experimentKey: 'rovo_jira_twg_cli_cta_v2',
		allowedCohorts: [RovoJiraTwgCliCtaCohort.TREATMENT],
		statsigParameterName: ROVO_JIRA_TWG_CLI_CTA_TREATMENT_PARAM,
	},
	'recent-downgrade-discount': {
		experimentKey: 'recent-churn-discount-experiment',
		allowedCohorts: ['variation'],
	},
};

export const getMessage = (
	initialMessages: { messageTemplateId: string; messageInstanceId: string }[],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
	const filteredMessages = ((initialMessages || []) as Message[]).filter(
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
		(dataMessage) => !excludedMessages.has(dataMessage.messageTemplateId),
	);

	for (const message of filteredMessages) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		const messageId = message.messageTemplateId;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		const experimentConfig = messageExperimentConfigs[messageId];

		if (!experimentConfig) {
			return message;
		}

		// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy experiment handling is retained without migration.
		const cohort = FeatureGates.getExperimentValue<ExperimentCohort>(
			experimentConfig.experimentKey,
			experimentConfig.statsigParameterName ?? 'cohort',
			'not-enrolled',
			{
				fireExperimentExposure: true,
			},
		);

		// Show message only if user is in one of the allowed variation cohorts
		if (isAllowedCohort(cohort, experimentConfig.allowedCohorts)) {
			return message;
		}
	}

	return undefined;
};
